import React, { useEffect, useState, useCallback, useRef } from "react";
import { Megaphone, Send, Trash2, Bold, Italic, List, Paperclip, Image as ImageIcon, Clock, X } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Select } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const AUDIENCE_TYPES = [
  { v: "all", l: "Semua Karyawan" },
  { v: "branch", l: "Cabang Tertentu" },
  { v: "position", l: "Posisi Tertentu" },
  { v: "role", l: "Role Sistem" },
  { v: "individual", l: "Karyawan Individu" },
];
const SYSTEM_ROLES = [
  { v: "OWNER", l: "Owner" }, { v: "SUPER_ADMIN", l: "Super Admin" }, { v: "HR_MANAGER", l: "HR Manager" },
  { v: "BRANCH_MANAGER", l: "Kepala Toko" }, { v: "FINANCE_MANAGER", l: "Finance Manager" },
];
const STATUS = { sent: { t: "success", l: "Terkirim" }, scheduled: { t: "warn", l: "Terjadwal" }, sending: { t: "info", l: "Mengirim" }, error: { t: "danger", l: "Gagal" } };

function Chips({ options, value, onChange, testid }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button key={o.v} type="button" onClick={() => toggle(o.v)}
          className={`rounded-full px-3 py-1 text-xs font-semibold ${value.includes(o.v) ? "bg-primary text-white" : "bg-stone-100 text-ink-soft hover:bg-stone-200"}`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

export default function HRAnnouncements() {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = ["SUPER_ADMIN", "OWNER", "HR_MANAGER"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [positions, setPositions] = useState([]);

  const [title, setTitle] = useState("");
  const [aud, setAud] = useState({ type: "all", branch_ids: [], positions: [], roles: [], employee_ids: [] });
  const [expires, setExpires] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [attachment, setAttachment] = useState(null); // {url,name}
  const [image, setImage] = useState(null); // {url}
  const [sending, setSending] = useState(false);
  const editorRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/announcements"); setRows(data.rows); }
    catch { toast.error("Gagal memuat pengumuman"); } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => {
    load();
    api.get("/hr/employees").then(({ data }) => {
      setEmployees(data.rows);
      const b = {}; const p = new Set();
      data.rows.forEach((e) => { if (e.branch_id) b[e.branch_id] = e.branch_name; if (e.position) p.add(e.position); });
      setBranches(Object.entries(b).map(([v, l]) => ({ v, l })));
      setPositions([...p].map((x) => ({ v: x, l: x })));
    }).catch(() => {});
  }, [load]);

  const exec = (cmd) => { document.execCommand(cmd, false, null); editorRef.current?.focus(); };

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/hr/announcements/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.is_image) setImage({ url: data.url }); else setAttachment({ url: data.url, name: data.name });
      toast.success("Lampiran diunggah");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengunggah"); }
  };

  const send = async () => {
    if (!title.trim()) return toast.error("Judul wajib diisi");
    const body_html = editorRef.current?.innerHTML || "";
    if (aud.type === "branch" && !aud.branch_ids.length) return toast.error("Pilih minimal satu cabang");
    if (aud.type === "position" && !aud.positions.length) return toast.error("Pilih minimal satu posisi");
    if (aud.type === "role" && !aud.roles.length) return toast.error("Pilih minimal satu role");
    if (aud.type === "individual" && !aud.employee_ids.length) return toast.error("Pilih minimal satu karyawan");
    setSending(true);
    try {
      const payload = {
        title: title.trim(), body_html, audience: aud,
        attachment_url: attachment?.url || null, attachment_name: attachment?.name || null,
        image_url: image?.url || null,
        expires_at: expires ? new Date(expires + "T23:59:59").toISOString() : null,
        scheduled_at: scheduleAt ? new Date(scheduleAt).toISOString() : null,
      };
      const { data } = await api.post("/hr/announcements", payload);
      toast.success(data.status === "scheduled" ? "Pengumuman dijadwalkan" : "Pengumuman terkirim");
      setTitle(""); setAud({ type: "all", branch_ids: [], positions: [], roles: [], employee_ids: [] });
      setExpires(""); setScheduleAt(""); setAttachment(null); setImage(null);
      if (editorRef.current) editorRef.current.innerHTML = "";
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengirim"); } finally { setSending(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/hr/announcements/${id}`); toast.success("Dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  return (
    <div data-testid="hr-announcements-page">
      <PageHeader title="Pengumuman HRD" subtitle="Kirim pengumuman ke karyawan via in-app & email (Owner & SuperAdmin selalu menerima)" icon={Megaphone} />

      {canManage && (
        <Card className="mb-5" data-testid="hr-ann-form">
          <h3 className="mb-3 font-heading font-bold text-ink">Buat Pengumuman</h3>
          <div className="space-y-3">
            <Input label="Judul" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="cth. Briefing Operasional" data-testid="hr-ann-title" />

            <div>
              <label className="cf-label">Isi (Rich Text)</label>
              <div className="mb-1 flex gap-1">
                <button type="button" onClick={() => exec("bold")} className="rounded-md border border-line p-1.5 hover:bg-stone-100" data-testid="hr-ann-bold"><Bold size={14} /></button>
                <button type="button" onClick={() => exec("italic")} className="rounded-md border border-line p-1.5 hover:bg-stone-100" data-testid="hr-ann-italic"><Italic size={14} /></button>
                <button type="button" onClick={() => exec("insertUnorderedList")} className="rounded-md border border-line p-1.5 hover:bg-stone-100" data-testid="hr-ann-list"><List size={14} /></button>
              </div>
              <div ref={editorRef} contentEditable data-testid="hr-ann-body"
                className="min-h-[110px] rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink focus:border-primary focus:outline-none [&_li]:ml-5 [&_li]:list-disc"
                suppressContentEditableWarning />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Select label="Audiens" value={aud.type} onChange={(e) => setAud({ ...aud, type: e.target.value })} data-testid="hr-ann-audience-type">
                {AUDIENCE_TYPES.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
              </Select>
              <Input label="Kedaluwarsa (opsional)" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="hr-ann-expires" />
            </div>

            {aud.type === "branch" && <div><label className="cf-label">Cabang</label><Chips options={branches} value={aud.branch_ids} onChange={(v) => setAud({ ...aud, branch_ids: v })} testid="hr-ann-branches" /></div>}
            {aud.type === "position" && <div><label className="cf-label">Posisi</label><Chips options={positions} value={aud.positions} onChange={(v) => setAud({ ...aud, positions: v })} testid="hr-ann-positions" /></div>}
            {aud.type === "role" && <div><label className="cf-label">Role Sistem</label><Chips options={SYSTEM_ROLES} value={aud.roles} onChange={(v) => setAud({ ...aud, roles: v })} testid="hr-ann-roles" /></div>}
            {aud.type === "individual" && (
              <div><label className="cf-label">Karyawan</label>
                <Chips options={employees.map((e) => ({ v: e.id, l: `${e.full_name} · ${e.position}` }))} value={aud.employee_ids} onChange={(v) => setAud({ ...aud, employee_ids: v })} testid="hr-ann-employees" />
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Jadwalkan (opsional — kosongkan untuk kirim sekarang)" type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="hr-ann-schedule" />
              <div>
                <label className="cf-label">Lampiran (PDF / Gambar)</label>
                <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="hr-ann-file-input" />
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="hr-ann-upload"><Paperclip size={14} /> Unggah</Button>
                  {image && <span className="flex items-center gap-1 text-xs text-success-text"><ImageIcon size={13} /> Gambar siap <button onClick={() => setImage(null)}><X size={12} /></button></span>}
                  {attachment && <span className="flex items-center gap-1 text-xs text-success-text"><Paperclip size={13} /> {attachment.name} <button onClick={() => setAttachment(null)}><X size={12} /></button></span>}
                </div>
              </div>
            </div>

            <Button onClick={send} loading={sending} data-testid="hr-ann-send"><Send size={15} /> {scheduleAt ? "Jadwalkan" : "Kirim Sekarang"}</Button>
          </div>
        </Card>
      )}

      {loading ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : (
        <Card data-testid="hr-ann-list">
          <h3 className="mb-3 font-heading font-bold text-ink">Riwayat Pengumuman</h3>
          {rows.length === 0 ? <p className="py-6 text-center text-sm text-ink-mute">Belum ada pengumuman</p> : (
            <ul className="divide-y divide-line">
              {rows.map((a) => (
                <li key={a.id} className="flex items-start justify-between gap-3 py-3" data-testid={`hr-ann-row-${a.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-ink">{a.title}</span>
                      <Badge tone={(STATUS[a.status] || STATUS.sent).t}>{(STATUS[a.status] || STATUS.sent).l}</Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-ink-mute">
                      Audiens: {a.audience?.type} · {a.recipients ? `${a.recipients.employees} karyawan` : "—"}
                      {a.scheduled_at && a.status === "scheduled" ? ` · dijadwalkan ${new Date(a.scheduled_at).toLocaleString("id-ID")}` : ""}
                      {a.expires_at ? ` · kedaluwarsa ${new Date(a.expires_at).toLocaleDateString("id-ID")}` : ""}
                    </p>
                  </div>
                  {canManage && <button onClick={() => del(a.id)} className="rounded-lg p-1.5 text-danger-text hover:bg-red-50" data-testid={`hr-ann-del-${a.id}`}><Trash2 size={15} /></button>}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
